<template>
<form @submit.prevent class="mb-0">

    <!-- form title -->
    <h2 v-if="formTitle" class="mb-3 text-center h2" v-html="formTitle"></h2>

    <!-- simple progress bar. it is configured in Application.vue in applicationSettings.progressBar -->
    <ProgressBar v-if="!!applicationSettings.progressBar.active && props.showProgressBar" />

    <div style="position:absolute;width:0;height:0;overflow:hidden;" aria-hidden="true">
        <input type="text" name="lc_contact" v-model="app.lc_contact" tabindex="-1" autocomplete="off" />
        <input type="text" name="lc_referral" v-model="app.lc_referral" tabindex="-1" autocomplete="off" />
    </div>

    <!-- lets loop through the entire array of steps declared in the JS file provided for this form -->
    <div v-for="(step, index) in props.formData.steps" :key="index + '-' + stepRemountKey">

        <!-- title shown at the top of the step -->
        <template v-if="index === activeStepIndex && !!step.topTitle">
            <div v-if="resolveText(step.topTitle, 'steps.titles')" v-html="resolveText(step.topTitle, 'steps.titles')"></div>
        </template>

        <!-- step -->
        <Transition name="fade" mode="out-in" @after-enter="onStepShown">

            <div class="" v-if="index === activeStepIndex" @click="_navigation.handleUserFormInteraction()">

                <!-- step title: a translation key, or a function of the answers returning one (like choices) -->
                <h3 v-if="resolveText(step.title, 'steps.titles')" class="my-4 text-center h4" v-html="resolveText(step.title, 'steps.titles')"></h3>

                <!-- hint section shown between title and fields -->
                <template v-if="!!step.hint">
                    <div class="hint-section d-flex align-items-start gap-2 p-3 mb-4 mx-auto" style="background-color: #ececec; border-radius: 8px; max-width: 600px;">
                        <span v-if="getHintIcon(step.hint) !== false" v-html="getHintIcon(step.hint)" class="mt-1" style="flex-shrink: 0; line-height: 0;"></span>
                        <div class="flex-grow-1">
                            <div v-if="resolveText(getHintContent(step.hint), 'steps.hint')" v-html="resolveText(getHintContent(step.hint), 'steps.hint')"></div>
                        </div>
                    </div>
                </template>

                <!-- edit summary step -->
                <div class="" v-if="!!activeStep.isStepReadOnly && !!activeStep.isStepEditable && !props.shouldEditActiveStep">
                    <div class="d-grid gap-2 d-md-flex justify-content-md-end my-2">
                        <button v-if="!editActiveStep" @click.prevent="editActiveStep = true;" id="btnEditStep"
                            type="button" class="btn btn-sm btn-outline-secondary">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                                class="bi bi-pencil" viewBox="0 0 16 16">
                                <path
                                    d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z">
                                </path>
                            </svg>
                            {{ getTranslatedText('__edit__', 'buttons') }}
                        </button>
                    </div>
                </div>

                <!-- text shown at the top of the step -->
                <div class="mb-3" v-if="resolveText(step.textTop, 'steps.textTop')" v-html="resolveText(step.textTop, 'steps.textTop')"></div>

                <div class="row">
                    <template v-for="(field) in step.fields" :key="field.name">
                        <!-- Force next columns to break to new line at md breakpoint and up -->
                        <div v-if="!!field.clearfixTop" class="d-none d-md-block"></div>
                        <div :class="field.fieldOuterClass ?? 'col-md-12'">
                            <hr v-if="!!field.dividerTop" class="border border-secondary border-1 opacity-40">

                            <h3 v-if="resolveText(field.textTop, 'fields.textTop')" v-html="resolveText(field.textTop, 'fields.textTop')"></h3>

                            <LcField v-if="!_validation.isHiddenField(field)" :fieldData="field"
                                @goToNextStep="_navigation.goToNextStepFromNestedComponent"
                                @enter="pressNextFromKeyboard"
                                :fieldValidator="validator[field.name]"
                                :shouldValidate="applicationSettings.shouldValidateForm"
                                :inputColor="applicationSettings.formStyle.inputColor"
                                :isStepReadOnly="step.isStepReadOnly && !editActiveStep && !props.shouldEditActiveStep"
                                :isStepEditable="step.isStepEditable && !!editActiveStep && !!props.shouldEditActiveStep"
                            />

                            <hr v-if="!!field.dividerBottom" class="border border-secondary border-1 opacity-40">

                            <div v-if="resolveText(field.textBottom, 'fields.textBottom')" v-html="resolveText(field.textBottom, 'fields.textBottom')"></div>
                        </div>
                        <!-- Force next columns to break to new line at md breakpoint and up -->
                        <div v-if="!!field.clearfixBottom" class="d-none d-md-block"></div>
                    </template>
                </div>

                <!-- mb-3 because .step-text-bottom strips the last paragraph's own bottom margin
                     (fine below the buttons, too tight above them) -->
                <div class="mt-2 mb-3 step-text-bottom" v-if="resolveText(step.textBottom, 'steps.textBottom')" v-html="resolveText(step.textBottom, 'steps.textBottom')"></div>

                <div v-if="!props.showSkipVerification">
                    <Recaptcha v-if="props.showRecaptchaVerification" @recaptchaValueChange="handleRecaptchaChange" />
                </div>

                <!-- dev mode: single Next button replaces all normal buttons -->
                <div v-if="applicationSettings._devMode && !applicationSettings._devShowRealButtons" class="row mt-2">
                    <div class="col-6 offset-3 col-md-4 offset-md-4" style="text-align:center;">
                        <a v-keyboard-button id="btnNextDev" class="btn btn-lg w-100 btn-outline-warning"
                            @click.prevent="_navigation.goToNextStep()">
                            {{ !isNestedForm && stepContainsNestedForm ? '[Dev] Skip Section' : '[Dev] Next' }}
                        </a>
                    </div>
                </div>

                <!-- action bottons section -->
                <div v-if="!activeStep.hideButtonsSection && (!applicationSettings._devMode || applicationSettings._devShowRealButtons)" class="row mt-2">
                    <div v-if="showSkipButtonsSection" class="col-6 col-md-4" style="text-align:center;">
                        <a v-if="props.showSkipRegistration && !!activeStep.isStepSkippable" v-keyboard-button id="btnSkip"
                            class="btn btn-lg w-100"
                            :class="'btn-' + (applicationSettings.formStyle.buttonColor?.skip ?? 'light')"
                            @click.prevent="executeUserWantsRegistrationDecision(false)">{{ getTranslatedText('__skip__', 'buttons') }}</a>

                        <a v-else-if="props.showSkipVerification" v-keyboard-button id="btnSkip" class="btn btn-lg w-100"
                            :class="'btn-' + (applicationSettings.formStyle.buttonColor?.skip ?? 'light')"
                            @click.prevent="_navigation.executeSkipVerification()">{{ getTranslatedText('__skip__', 'buttons') }}</a>

                        <a v-else-if="!!activeStep.isStepSkippable" v-keyboard-button id="btnSkip" class="btn btn-lg w-100"
                            :class="'btn-' + (applicationSettings.formStyle.buttonColor?.skip ?? 'light')"
                            @click.prevent="_navigation.goToNextStep(!!activeStep.isStepSkippable)">{{ getTranslatedText('__skip__', 'buttons') }}</a>
                    </div>

                    <div :class="showSkipButtonsSection ? 'col-6 col-md-4' : 'col-6 offset-3 col-md-4 offset-md-4'" style="text-align:center;">
                        <a v-if="isLastStep || isLastApplicationStep" v-keyboard-button :ref="rememberSubmitButton" class="btn btn-lg w-100"
                            :class="!!step.nextButtonClass && step.nextButtonClass.length>0 ?
                                step.nextButtonClass :
                                (!skipRecaptcha || isSubmitting) ? 'btn-secondary disabled' : 'btn-' + (applicationSettings.formStyle.buttonColor?.next ?? 'primary')"
                            :disabled="!skipRecaptcha || isSubmitting"
                            @click.prevent="(skipRecaptcha && !isSubmitting) ? _navigation.executeFlowCompleted() : true">
                            {{ actionButtonLabel(step) }}</a>
                        <a v-else v-keyboard-button id="btnNext" class="btn btn-lg w-100"
                            :class="!!step.nextButtonClass && step.nextButtonClass.length>0 ?
                                step.nextButtonClass :
                                !skipRecaptcha ? 'btn-secondary disabled' : 'btn-' + (applicationSettings.formStyle.buttonColor?.next ?? 'primary')"
                            :disabled="!skipRecaptcha"
                            @click.prevent="onNextClick">
                            {{ actionButtonLabel(step) }}</a>
                    </div>

                </div>

                <div class="mt-4 step-text-bottom" v-if="resolveText(step.textBottomBelowActionButtons, 'steps.textBottomBelowActionButtons')" v-html="textBelowActionButtons(step)"></div>

                <!-- CASL: terms of use / privacy policy consent, kept separate from the email opt-in
                     above and shown once, on the first step the applicant actually reaches. -->
                <div class="mt-4 step-text-bottom" v-if="showTermsPrivacyConsent"
                    v-html="getTranslatedText('__terms_privacy_consent__', 'steps.textBottomBelowActionButtons')"></div>

            </div>
        </Transition>
        <!-- /step -->

    </div>

    <!-- notice (modal): overlay shown when step becomes active -->
    <Transition name="fade">
        <div v-if="!!activeStep.notice && getNoticeDisplay(activeStep.notice) === 'modal' && getNoticeContent(activeStep.notice) && !noticeModalDismissed"
             class="notice-modal-overlay" @click.self="dismissNoticeModal">
            <div class="notice-modal-card">
                <div class="notice-modal-body" v-html="getNoticeContent(activeStep.notice)"></div>
                <button type="button" class="notice-modal-btn" @click="dismissNoticeModal">Got it</button>
            </div>
        </div>
    </Transition>

<!--    This is a development tool that shows an overview of all steps and allows jumping between them, filling/clearing values, toggling real buttons, and resetting the form. -->
<!--    It is only visible when _devMode is enabled in applicationSettings (APP_DEBUG=true & dev_step needed in URL)-->
    <DevStepPanel
        v-if="applicationSettings._devMode && !isNestedForm"
        :steps="props.formData.steps"
        :activeStepIndex="activeStepIndex"
        :productName="props.productName"
        @jump-to-step="jumpToStep"
        @fill-step="fillStep"
        @clear-step="clearStep"
        @toggle-real-buttons="applicationSettings._devShowRealButtons = $event"
        @next-step="_navigation.goToNextStep()"
        @toggle-force-hidden="devForceHidden = $event"
        @toggle-force-validation="applicationSettings._devForceValidation = $event"
        @toggle-allow-requests="applicationSettings._devAllowRequests = $event"
        @reset-form="resetForm"
    />

<!--    This component is used to display special offers after certain steps.-->
<!--    The offers are determined by the checkOffers method from the useOfferCheck composable, which is called whenever the active step changes.-->
<!--    If an offer is active, it will be rendered here. The offer components can emit a "close" event to dismiss themselves.-->
    <component
        v-if="activeOffer"
        :is="activeOffer.component"
        :app="app"
        :product="productName"
        :step="activeStepIndex"
        @close="dismissOffer"
    />
</form>
</template>

<script setup>

import ProgressBar from '@/components/form/ProgressBar.vue';
import LcField from '@/components/form/LcField.vue';
import Recaptcha from '@/components/form/services/Recaptcha.vue';
import DevStepPanel from '@/components/dev/DevStepPanel.vue';

import { inject, provide, ref, computed, onMounted, onBeforeUnmount, watch, watchEffect, nextTick } from 'vue';

import { useLoadingAnimation } from '@/composables/useLoadingAnimation.js';
import { useFormSubmission } from '@/composables/useFormSubmission.js';
import { useFormNavigation } from '@/composables/useFormNavigation.js';
import { useFormValidation } from '@/composables/useFormValidation.js';
import { useInjectedApplicationSettings } from '@/composables/useApplicationSettingsProvider.js';
import { useOfferCheck } from '@/composables/useOfferCheck.js';
import { withConsentButtonLabel } from '@/form/helpers/consent-button.js';
import { vKeyboardButton } from '@/directives/keyboard-button.js';
import { focusFirstField } from '@/utils/scroll.js';

// Detect nested LcForm via provide/inject
const isNestedForm = inject('isInsideLcForm', false);
provide('isInsideLcForm', true);

// Props & Emits
const props = defineProps({
    formData: {
        type: Object,
        required: true,
    },
    formSettings: {
        type: Object,
        required: false,
    },
    showSkipVerification: {
        type: Boolean,
        required: false,
        default: false,
    },
    showSkipRegistration: {
        type: Boolean,
        required: false,
        default: false,
    },
    showRecaptchaVerification: {
        type: Boolean,
        required: false,
        default: false,
    },
    productName: {
        type: String,
        default: null,
    },
    formTitle: {
        type: String,
        required: false,
        default: null,
    },
    shouldEditActiveStep: {
        type: Boolean,
        required: false,
        default: false,
    },
    showProgressBar: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const emit = defineEmits(["completed"]);

// Injected reactive applicationSettings from Application.vue
const applicationSettings = useInjectedApplicationSettings();

// Inject
const { app, config, persistLocally, updateApplication, processedApplication, getTranslatedText } = inject('app');

// Reactive state
const validator = ref({});
const activeStep = ref({});
const hiddenFields = ref({});
const hiddenFieldsIfCondition = ref({});
const activeStepIndex = ref(0);
const shouldSkipNextStep = ref(false);
const isRecaptchaSolved = ref(false);
const editActiveStep = ref(false);
const isSubmitting = ref(false);
const rotatingMessagesInterval = ref(null);
const loadingAnimationInterval = ref(null);
const shouldAutoSkipStep = ref(false);
const stepRemountKey = ref(0);
const noticeModalDismissed = ref(false);

// CASL requires the terms/privacy consent to stand apart from the express email opt-in, so it
// renders on its own on the first step of the form. It cannot be pinned to schema step 0: pq
// hides personal_loan's first step (hideIfCondition) and any prepop flow auto-skips steps whose
// fields arrive prefilled (useFormValidation.hideField), either of which would silently drop the
// consent. Capture the first step that is actually SHOWN instead -- same approach as
// stepOnFirstUserClick below. Empty text (US brands) means it never renders.
const firstShownStepIndex = ref(null);
const termsPrivacyConsentText = window.App?.translation_keys?.terms_privacy_consent ?? '';
const showTermsPrivacyConsent = computed(() =>
    !isNestedForm
    && !!termsPrivacyConsentText
    && firstShownStepIndex.value !== null
    && firstShownStepIndex.value === activeStepIndex.value
);

// Offer check system
const { activeOffer, checkOffers, dismiss: dismissOffer } = useOfferCheck();

// Composable initialization
const _loading = useLoadingAnimation({
    applicationSettings,
    app,
    activeStep,
    rotatingMessagesInterval,
    loadingAnimationInterval,
});

const _submission = useFormSubmission({
    app,
    applicationSettings,
    updateApplication,
    processedApplication,
    isSubmitting,
    loading: _loading,
});

const devStepOverride = ref(false);
const devForceHidden = ref(false);

const _validation = useFormValidation({
    app,
    config,
    applicationSettings,
    updateApplication,
    activeStep,
    validator,
    hiddenFields,
    hiddenFieldsIfCondition,
    editActiveStep,
    loading: _loading,
    shouldAutoSkipStep,
    devStepOverride,
    devForceHidden,
});

const _navigation = useFormNavigation({
    app,
    applicationSettings,
    persistLocally,
    formData: () => props.formData,
    activeStepIndex,
    activeStep,
    shouldSkipNextStep,
    emit,
    validateForm: (...args) => _validation.validateForm(...args),
    submission: _submission,
    loading: _loading,
    getComponentMethod: (name) => _methods[name],
});

// Method registry for string-based step callbacks (e.g. beforeNextStep: "executeSubmitApplication")
const _methods = {
    // submission
    executeEmailRegistrationCheck: _submission.executeEmailRegistrationCheck,
    executeEmailAuthentication: _submission.executeEmailAuthentication,
    enablePartialRequestPerStep: _submission.enablePartialRequestPerStep,
    executeCreatePartialApp: _submission.executeCreatePartialApp,
    // navigation
    executeSkipIfAuthenticated: _navigation.executeSkipIfAuthenticated,
    executeClearPassword: _navigation.executeClearPassword,
    // validation
    initializeValuesForVerification,
    // component-local
    executeSkipIfUnregistered,
    executeUserWantsRegistrationDecision,
    executeUserWantsVerificationDecision,
};

// Template helper: resolves function-or-string text then translates
function resolveText(textOrFn, translationKey) {
    if (!textOrFn) {
        return null;
    }
    if (typeof textOrFn === 'function') {
        const result = textOrFn({ ...app.value });
        if (result === false) {
            return null;
        }
        return getTranslatedText(result, translationKey);
    }
    return getTranslatedText(textOrFn, translationKey);
}

// Template helper: the label of the step's main button -- "Submit" on the last step unless the
// step sets nextButtonText, "Next" otherwise.
function actionButtonLabel(step) {
    let text = '__next__';
    if (!!step.nextButtonText && step.nextButtonText.length > 0) {
        text = step.nextButtonText;
    } else if (isLastStep.value || isLastApplicationStep.value) {
        text = submitButtonText.value;
    }
    return getTranslatedText(text, 'buttons');
}

// Template helper: the text under the buttons, with the consent naming the button this step
// actually shows (see consent-button.js). A step without buttons keeps the copy as written.
function textBelowActionButtons(step) {
    const html = resolveText(step.textBottomBelowActionButtons, 'steps.textBottomBelowActionButtons');
    if (step.hideButtonsSection) {
        return html;
    }
    return withConsentButtonLabel(html, actionButtonLabel(step));
}

// Notice helpers
function getNoticeDisplay(notice) {
    if (typeof notice === 'object' && notice !== null && !Array.isArray(notice)) {
        return notice.display || 'inline';
    }
    return 'inline';
}

function getNoticeContent(notice) {
    return resolveText(getHintContent(notice), 'steps.notice');
}

// Sync inline notice content to applicationSettings so MainApp can render it above the card
watchEffect(() => {
    const step = activeStep.value;
    if (step?.notice && getNoticeDisplay(step.notice) === 'inline') {
        applicationSettings.currentNotice = getNoticeContent(step.notice);
    } else {
        applicationSettings.currentNotice = null;
    }
});

function dismissNoticeModal() {
    noticeModalDismissed.value = true;
}

// Methods
function getHintContent(hint) {
    if (typeof hint === 'object' && hint !== null && !Array.isArray(hint)) {
        return hint.content;
    }
    return hint;
}

function getHintIcon(hint) {
    const icons = {
        'circle-info': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" class="text-secondary" style="width: 1rem; height: 1rem;"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>',
        'lightbulb': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" class="text-warning" style="width: 1rem; height: 1rem;"><path d="M297.2 248.9C311.6 228.3 320 203.2 320 176c0-70.7-57.3-128-128-128S64 105.3 64 176c0 27.2 8.4 52.3 22.8 72.9c3.7 5.3 8.1 11.3 12.8 17.7c0 0 0 0 0 0c12.9 17.7 28.3 38.9 39.8 59.8c10.4 19 15.7 38.8 18.3 57.5H109c-2.2-12-5.9-23.7-11.8-34.5c-9.9-18-22.2-34.9-34.5-51.8c0 0 0 0 0 0s0 0 0 0c-5.2-7.1-10.4-14.2-15.4-21.4C27.6 247.9 16 213.3 16 176C16 78.8 94.8 0 192 0s176 78.8 176 176c0 37.3-11.6 71.9-31.4 100.3c-5 7.2-10.2 14.3-15.4 21.4c0 0 0 0 0 0s0 0 0 0c-12.3 16.8-24.6 33.7-34.5 51.8c-5.9 10.8-9.6 22.5-11.8 34.5H226.4c2.6-18.7 7.9-38.6 18.3-57.5c11.5-20.9 26.9-42.1 39.8-59.8c0 0 0 0 0 0s0 0 0 0s0 0 0 0c4.7-6.4 9-12.4 12.7-17.7zM192 128c-26.5 0-48 21.5-48 48c0 8.8-7.2 16-16 16s-16-7.2-16-16c0-44.2 35.8-80 80-80c8.8 0 16 7.2 16 16s-7.2 16-16 16zm0 384c-44.2 0-80-35.8-80-80V416H272v16c0 44.2-35.8 80-80 80z"/></svg>',
        'shield': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" class="text-success" style="width: 1rem; height: 1rem;"><path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0z"/></svg>',
        'warning': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" class="text-warning" style="width: 1rem; height: 1rem;"><path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/></svg>',
    };
    if (typeof hint === 'object' && hint !== null && !Array.isArray(hint)) {
        if (hint.icon === false) {
            return false;
        }
        if (typeof hint.icon === 'string') {
            return icons[hint.icon] || false;
        }
    }
    return icons['circle-info'];
}

function handleRecaptchaChange(recaptchaResponse) {
    isRecaptchaSolved.value = recaptchaResponse;
}


function initializeValuesForVerification() {
    activeStep.value = {};
    hiddenFields.value = {};
    hiddenFieldsIfCondition.value = {};
    activeStepIndex.value = 0;
}

function executeSkipIfUnregistered() {
    if (!applicationSettings.isUserRegistered) {
        if (activeStepIndex.value === (props.formData.steps.length - 1)) {
            let shouldSkipValidation = true;
            _navigation.executeFlowCompleted(shouldSkipValidation);
        }
        shouldSkipNextStep.value = true;
    }
}

function executeUserWantsRegistrationDecision(skippedRegistration = null) {
    let userResponse = false;
    if (skippedRegistration === null || (typeof skippedRegistration !== 'boolean' && !skippedRegistration)) {
        let userWantsRegistration = _submission.getFieldValue('wants_to_create_account');
        userResponse = userWantsRegistration === 'yes';
    }
    if (!userResponse) {
        _navigation.executeSkipRegistration();
    } else {
        applicationSettings.userWantsRegistration = true;
    }
}

function executeUserWantsVerificationDecision() {
    let userWantsVerification = _submission.getFieldValue('wants_verification');
    applicationSettings.userWantsVerification = userWantsVerification === 'yes';
}

function jumpToStep(index) {
    activeStepIndex.value = index;
    activeStep.value = props.formData.steps[index];
}

function fillStep(values) {
    Object.entries(values).forEach(([key, value]) => {
        app.value[key] = value;
    });
    // Force step re-mount so components like Address re-initialize with new values
    stepRemountKey.value++;
}

function clearStep(fieldNames) {
    fieldNames.forEach((key) => {
        app.value[key] = null;
    });
}

// --- Keyboard ---

// The submit button of the step on screen (only the last step renders one), for Enter.
let submitButton = null;
function rememberSubmitButton(el) {
    submitButton = el;
}

// Set while a step is being moved on from the keyboard: the step that appears then gets the focus.
let focusNextStep = false;

/**
 * Enter in a text field (LcField's `enter`) does what the step's own button does. The browser
 * won't do it: with the honeypot inputs the form has several text fields and no submit button, so
 * there is no implicit submission. On the step that sends the lead, Enter only moves the focus to
 * that button, so the lead goes out from the button its consent text names ("by clicking"), never
 * from an Enter meant to finish the last field.
 */
function pressNextFromKeyboard() {
    if (activeStep.value.hideButtonsSection) {
        return;
    }
    if (isLastStep.value || isLastApplicationStep.value) {
        submitButton?.focus();
        return;
    }
    if (skipRecaptcha.value) {
        goToNextStepFromKeyboard();
    }
}

async function goToNextStepFromKeyboard() {
    const stepBefore = activeStepIndex.value;
    focusNextStep = true;
    await _navigation.goToNextStep();
    // Stayed put: a field failed and has the focus now. A later mouse Next must not move it.
    if (activeStepIndex.value === stepBefore) {
        focusNextStep = false;
    }
}

// Enter or Space on Next (v-keyboard-button) clicks it with event.detail 0; a mouse click or a tap
// has 1 or more.
function onNextClick(event) {
    if (!skipRecaptcha.value) {
        return;
    }
    if (event.detail === 0) {
        goToNextStepFromKeyboard();
    } else {
        _navigation.goToNextStep();
    }
}

// The field that had the focus left with the old step. Put the focus in the new one rather than
// leave a keyboard user to Tab in again from the top of the page.
function onStepShown(stepElement) {
    if (!focusNextStep) {
        return;
    }
    focusNextStep = false;
    focusFirstField(stepElement);
}

function resetForm() {
    // Clear all field values across every step
    props.formData.steps.forEach((step) => {
        (step.fields || []).forEach((field) => {
            if (field.name) {
                app.value[field.name] = null;
            }
        });
    });

    // Determine target step: use dev_step from URL params if available, otherwise step 0
    let targetIndex = 0;
    const urlParams = new URLSearchParams(window.location.search);
    const devStepParam = urlParams.get('dev_step');
    if (devStepParam) {
        const parsed = /^\d+$/.test(devStepParam) ? parseInt(devStepParam, 10) - 1 : NaN;
        if (!isNaN(parsed) && parsed >= 0 && parsed < props.formData.steps.length) {
            targetIndex = parsed;
        } else {
            const found = props.formData.steps.findIndex(
                step => step.fields?.some(f => f.name === devStepParam)
            );
            if (found >= 0) {
                targetIndex = found;
            }
        }
    }

    devStepOverride.value = true;
    activeStepIndex.value = targetIndex;
    activeStep.value = props.formData.steps[targetIndex];
    stepRemountKey.value++;
}

// Computed
const isLastStep = computed(() => _navigation.isLastStep());
const submitButtonText = computed(() => props.formSettings ? props.formSettings.submitButtonText : '__next__');
const skipRecaptcha = computed(() => !props.showRecaptchaVerification || isRecaptchaSolved.value);
const isLastApplicationStep = computed(() => _navigation.isLastApplicationStep());
const showSkipButtonsSection = computed(() =>
    (props.showSkipRegistration && !!activeStep.value.isStepSkippable) ||
    props.showSkipVerification ||
    !!activeStep.value.isStepSkippable
);
const NESTED_FORM_FIELD_TYPES = ['memberenrollment'];
const stepContainsNestedForm = computed(() =>
    activeStep.value.fields?.some(f => NESTED_FORM_FIELD_TYPES.includes(f.type))
);

// Lifecycle
onMounted(() => {
    activeStep.value = props.formData.steps[activeStepIndex.value];
    applicationSettings.partialRequestsCount = applicationSettings.partialRequestsCount ?? 0;

    // Dev tool: skip to a specific step via ?dev_step=N or ?dev_step=field_name
    if (window.App?.app_debug && app.value?.dev_step) {
        const devStepParam = String(app.value.dev_step);
        let targetIndex = /^\d+$/.test(devStepParam) ? parseInt(devStepParam, 10) - 1 : NaN;

        // Support field name lookup (e.g., dev_step=member_enrollment)
        if (isNaN(targetIndex)) {
            targetIndex = props.formData.steps.findIndex(
                step => step.fields?.some(f => f.name === devStepParam)
            );
        }

        if (isNaN(targetIndex) || targetIndex < 0 || targetIndex >= props.formData.steps.length) {
            console.warn(`[dev_step] "${devStepParam}" not found, defaulting to step 1`);
            targetIndex = 0;
        }

        console.table(
            props.formData.steps.map((s, i) => ({
                step: i + 1,
                fields: s.fields?.map(f => f.name).join(', ') || '(none)',
                title: s.title || '(no title)',
            }))
        );
        console.log(`[dev_step] Jumping to step ${targetIndex + 1}`);

        applicationSettings._devMode = true;
        devStepOverride.value = true;
        activeStepIndex.value = targetIndex;
        activeStep.value = props.formData.steps[targetIndex];
        delete app.value.dev_step;
    }
});

onBeforeUnmount(() => {
    _loading.cleanup();
});

// Watchers
watch(activeStep, () => {
    noticeModalDismissed.value = false;
    _navigation.checkIfLastStep();
    _validation.initStep();
    applicationSettings.progressBar.isFinalStep = !!isLastApplicationStep.value;

    const visibleSteps = props.formData.steps.filter(
        s => typeof s.hideIfCondition !== 'function' || !s.hideIfCondition({ ...app.value })
    ).length;
    applicationSettings.progressBar.totalSteps = visibleSteps;
    applicationSettings.progressBar.step += 1;

    // Capture the first visible step for showPrepopMessage. We can't rely on a
    // click reaching the step-div @click handler — e.g. on a prepop flow where
    // the address or contact step is the first visible step, the user sees
    // pre-filled data and clicks Next directly without ever clicking inside
    // the step content. nextTick waits for initStep's await + any auto-skip
    // decision so we only capture on steps that are actually shown.
    nextTick(() => {
        if (
            applicationSettings.stepOnFirstUserClick === null
            && !shouldAutoSkipStep.value
        ) {
            applicationSettings.stepOnFirstUserClick = applicationSettings.progressBar.step;
        }

        if (firstShownStepIndex.value === null && !shouldAutoSkipStep.value) {
            firstShownStepIndex.value = activeStepIndex.value;
        }
    });
});

watch(activeStepIndex, (newIndex, oldIndex) => {
    if (oldIndex !== undefined && props.productName) {
        checkOffers(props.productName, oldIndex, { ...app.value }, props.formData.steps.length);
    }
});

watch(hiddenFieldsIfCondition, (val) => {
    _validation.handleHiddenFieldsIfConditionChange(val);
}, { deep: true });

watch(shouldAutoSkipStep, (val) => {
    if (val) {
        // The skip decision can land after the nextTick capture above, so the step being
        // skipped may already have claimed the consent. Release it -- the step the applicant
        // actually lands on takes it instead. Repeats correctly across a run of skipped steps.
        if (firstShownStepIndex.value === activeStepIndex.value) {
            firstShownStepIndex.value = null;
        }

        shouldAutoSkipStep.value = false;
        _navigation.goToNextStepFromNestedComponent(true);
    }
});

</script>

<style scoped>
.fade-enter-active {
    transition: opacity 1s ease;
}

.fade-leave-active {
    opacity: 0;
}

.fade-enter-from {
    opacity: 0;
}

.fade-leave-to {
    opacity: 0;
}

.step-text-bottom :deep(> *:last-child) {
    margin-bottom: 0;
}

.notice-modal-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.45);
    z-index: 9999;
    padding: 1rem;
}

.notice-modal-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    max-width: 420px;
    width: 100%;
    padding: 2rem 2rem 1.5rem;
    text-align: center;
    animation: noticeModalIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.notice-modal-body {
    font-size: 1.05rem;
    line-height: 1.6;
    color: #333;
    margin-bottom: 1.5rem;
}

.notice-modal-body :deep(small) {
    display: block;
    margin-top: 0.35rem;
    font-size: 0.9rem;
    color: #666;
}

.notice-modal-btn {
    display: inline-block;
    padding: 0.5rem 2.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #fff;
    background: var(--bs-primary, #0d6efd);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.1s ease;
}
.notice-modal-btn:hover { filter: brightness(1.1); }
.notice-modal-btn:active { transform: scale(0.97); }

@keyframes noticeModalIn {
    0% { opacity: 0; transform: scale(0.7); }
    100% { opacity: 1; transform: scale(1); }
}
</style>
